/**
 * Intersection Observer functionality for automatic animation initialization
 */

import type { IntersectionObserverOptions } from './types';

/**
 * Creates an IntersectionObserver callback function that handles in-view class management
 */
function createObserverCallback(
  once: boolean,
  handleChildren = false
): (
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver
) => void {
  return (entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in-view');
        // Also add to all children with .css-motion class if requested
        if (handleChildren && entry.target instanceof HTMLElement) {
          entry.target.querySelectorAll('.css-motion').forEach(child => {
            child.classList.add('in-view');
          });
        }
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        entry.target.classList.remove('in-view');
        // Also remove from all children if requested
        if (handleChildren && entry.target instanceof HTMLElement) {
          entry.target.querySelectorAll('.css-motion').forEach(child => {
            child.classList.remove('in-view');
          });
        }
      }
    });
  };
}

/**
 * Observes a single element and adds/removes the 'in-view' class based on visibility
 *
 * @param element - The element to observe
 * @param options - IntersectionObserver options
 * @returns A cleanup function to disconnect the observer
 */
export function observeElement(
  element: HTMLElement,
  options: IntersectionObserverOptions = {}
) {
  const { threshold = 0.1, rootMargin = '0px', once = true } = options;

  const callbackFactory = createObserverCallback(once, true);
  const observer = new IntersectionObserver(
    entries => callbackFactory(entries, observer),
    { threshold, rootMargin }
  );

  observer.observe(element);

  return () => {
    observer.disconnect();
  };
}

/**
 * Automatically initializes all .css-motion--view elements in the page
 * Call this once in your root layout or use as an action on the body
 */
export function initObserver(options: IntersectionObserverOptions = {}) {
  const { threshold = 0.1, rootMargin = '0px', once = true } = options;

  const callbackFactory = createObserverCallback(once, false);
  const observer = new IntersectionObserver(
    entries => callbackFactory(entries, observer),
    { threshold, rootMargin }
  );

  // Observe all existing .css-motion--view elements
  document.querySelectorAll('.css-motion--view').forEach(element => {
    observer.observe(element);
  });

  // Watch for new .css-motion--view elements added to the DOM
  const mutationObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node instanceof HTMLElement) {
          if (node.classList.contains('css-motion--view')) {
            observer.observe(node);
          }
          // Also check children
          node.querySelectorAll('.css-motion--view').forEach(element => {
            observer.observe(element);
          });
        }
      });
    });
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  return {
    destroy: () => {
      observer.disconnect();
      mutationObserver.disconnect();
    },
  };
}
